import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { useOrganizationPackageLabels } from '../../../hooks/api/packageLabels';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import { FormLabel, FormLabelVariant } from '../../generic';
import FilterCard from '../../generic/Filter/FilterCard';
import useFetchPackages from '../hooks/useFetchPackages';
import usePackageManagers from '../hooks/usePackageManagers';
import type { Fetcher } from '../../../../shared/fetchers';
import type { SelectOption } from '../../../types';
import type { WithResetSelectedPackage } from '../PackageInventoryContainer';

import PackageLabelsFilter from './PackageLabelsFilter';
import PackageLocatorFilter from './PackageLocatorFilter';
import PackageManagerFilter from './PackageManager';

type Props = {
  areFetchersLoading: boolean;
  fetcherOptions: Array<SelectOption<Fetcher>>;
  selectedFetchers: Array<SelectOption<Fetcher>>;
  arePackageLabelsLoading: boolean;
  packageLabelOptions: string[];
  selectedPackageLabels: string[];
  selectedLocators: Array<SelectOption<string>>;
  setFilters: (...args: unknown[]) => void;
};

/**
 * PackageInventoryFiltersProject has filters related to filtering
 * packages by package manager (fetcher), labels, and locator.
 */
const PackageInventoryFiltersPackages: VoidFunctionComponent<Props> = ({
  areFetchersLoading,
  fetcherOptions,
  selectedFetchers,
  arePackageLabelsLoading,
  packageLabelOptions,
  selectedPackageLabels,
  selectedLocators,
  setFilters,
}) => (
  <FilterCard id="package-inventory-package-filters" title="Package">
    <FormLabel label="Package manager" variant={FormLabelVariant.Filter}>
      <PackageManagerFilter
        isLoading={areFetchersLoading}
        fetcherOptions={fetcherOptions}
        selectedFetchers={selectedFetchers}
        setFilters={setFilters}
      />
    </FormLabel>
    <FormLabel label="Labels" variant={FormLabelVariant.Filter}>
      <PackageLabelsFilter
        isLoading={arePackageLabelsLoading}
        packageLabelOptions={packageLabelOptions}
        selectedPackageLabels={selectedPackageLabels}
        setFilters={setFilters}
      />
    </FormLabel>
    <FormLabel label="Locator" variant={FormLabelVariant.Filter}>
      <PackageLocatorFilter locators={selectedLocators} setFilters={setFilters} />
    </FormLabel>
  </FilterCard>
);

/**
 * Stateful container for the package filters as they are on the main /packages page.
 */
const PackageInventoryFiltersPackagesContainer: VoidFunctionComponent<{
  withResetSelectedPackage: WithResetSelectedPackage;
}> = ({
  withResetSelectedPackage,
}) => {
  const { fetchers: fetcherOptions, isLoading: areFetchersLoading } = usePackageManagers();
  const selectedFetchers = useSelector(PackageInventoryStore.selectors.getFiltersFetchersOptions);

  // Load org package labels with the useOrganizationPackageLabels hook.
  const { data: packageLabelsResponse, isLoading: arePackageLabelsLoading } = useOrganizationPackageLabels();
  const packageLabels = packageLabelsResponse?.packageLabels ?? [];
  const packageLabelOptions = packageLabels.map((packageLabel) => packageLabel.name);
  const selectedPackageLabels = useSelector(PackageInventoryStore.selectors.getFiltersPackageLabels);

  const selectedLocators = useSelector(PackageInventoryStore.selectors.getFiltersLocatorsOptions);

  const setFilters = useFetchPackages(PackageInventoryStore.actions.setFilters);

  return (
    <PackageInventoryFiltersPackages
      areFetchersLoading={areFetchersLoading}
      fetcherOptions={fetcherOptions}
      selectedFetchers={selectedFetchers}
      arePackageLabelsLoading={arePackageLabelsLoading}
      packageLabelOptions={packageLabelOptions}
      selectedPackageLabels={selectedPackageLabels}
      selectedLocators={selectedLocators}
      setFilters={withResetSelectedPackage(setFilters)}
    />
  );
};

export default PackageInventoryFiltersPackagesContainer;
